import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Package, CreditCard, MapPin, QrCode, Barcode, Copy, Check, Truck, Loader2 } from 'lucide-react';
import { getApiUrl } from '@/utils/api';
import CheckoutUpsell from '@/components/CheckoutUpsell';

const API = getApiUrl();

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart, sessionId, refreshCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [paymentResult, setPaymentResult] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Shipping states
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [shippingCalculated, setShippingCalculated] = useState(false);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(199);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cpf_cnpj: '',
    postal_code: '',
    address: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  const cartTotal = getCartTotal();
  const shippingCost = selectedShipping?.free_shipping ? 0 : (selectedShipping?.price || 0);
  const orderTotal = cartTotal + shippingCost;

  const handleInputChange = (e) => {
    let value = e.target.value;
    const name = e.target.name;
    
    // Format CPF/CNPJ
    if (name === 'cpf_cnpj') {
      value = value.replace(/\D/g, '');
      if (value.length <= 11) {
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      } else {
        value = value.replace(/(\d{2})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1/$2');
        value = value.replace(/(\d{4})(\d{1,2})$/, '$1-$2');
      }
    }
    
    // Format CEP
    if (name === 'postal_code') {
      value = value.replace(/\D/g, '');
      if (value.length > 5) {
        value = value.replace(/(\d{5})(\d)/, '$1-$2');
      }
      value = value.slice(0, 9);
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Reset shipping when postal code changes
    if (name === 'postal_code') {
      setShippingCalculated(false);
      setSelectedShipping(null);
      setShippingOptions([]);
    }
  };

  const calculateShipping = async () => {
    const postalCode = formData.postal_code.replace(/\D/g, '');
    
    if (postalCode.length !== 8) {
      toast.error('Digite um CEP válido');
      return;
    }
    
    setLoadingShipping(true);
    
    try {
      const response = await axios.post(`${API}/shipping/calculate`, {
        postal_code: postalCode,
        products: cart.map(item => ({
          quantity: item.quantity,
          weight: 0.3
        })),
        cart_total: cartTotal
      });
      
      if (response.data.success) {
        setShippingOptions(response.data.options);
        setFreeShippingThreshold(response.data.free_shipping_threshold);
        setShippingCalculated(true);
        
        // Auto-select cheapest option
        if (response.data.options.length > 0) {
          setSelectedShipping(response.data.options[0]);
        }
        
        if (response.data.free_shipping_eligible) {
          toast.success('Parabéns! Você ganhou frete grátis!');
        }
      }
    } catch (error) {
      console.error('Shipping calculation error:', error);
      const shippingDetail = error.response?.data?.detail;
      toast.error(typeof shippingDetail === 'string' ? shippingDetail : 'Erro ao calcular frete');
    } finally {
      setLoadingShipping(false);
    }
  };

  // Auto-calculate shipping when CEP is complete
  useEffect(() => {
    const postalCode = formData.postal_code.replace(/\D/g, '');
    if (postalCode.length === 8 && !shippingCalculated) {
      calculateShipping();
    }
  }, [formData.postal_code]);

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.cpf_cnpj) {
      toast.error('Preencha todos os campos obrigatórios');
      return false;
    }
    
    if (!formData.postal_code || !formData.address || !formData.neighborhood || !formData.city || !formData.state) {
      toast.error('Preencha o endereço completo');
      return false;
    }
    
    if (!selectedShipping) {
      toast.error('Selecione uma opção de frete');
      return false;
    }
    
    const cpfCnpjClean = formData.cpf_cnpj.replace(/\D/g, '');
    if (cpfCnpjClean.length !== 11 && cpfCnpjClean.length !== 14) {
      toast.error('CPF/CNPJ inválido');
      return false;
    }
    
    return true;
  };

  const createOrder = async () => {
    const orderData = {
      items: cart.map(item => ({
        product_id: item.product.id,
        product_name: item.product.name,
        size: item.size,
        quantity: item.quantity,
        price: item.product.price,
        session_id: sessionId
      })),
      customer_name: formData.name,
      customer_email: formData.email,
      customer_phone: formData.phone,
      customer_document: formData.cpf_cnpj,
      customer_address: formData.address,
      customer_number: formData.number || 'S/N',
      customer_complement: formData.complement,
      customer_neighborhood: formData.neighborhood,
      customer_city: formData.city,
      customer_state: formData.state,
      customer_postal_code: formData.postal_code,
      shipping_service_id: selectedShipping?.id != null ? String(selectedShipping.id) : null,
      shipping_name: selectedShipping?.name,
      shipping_price: shippingCost,
      subtotal: cartTotal,
      total: orderTotal
    };

    const response = await axios.post(`${API}/orders`, orderData);
    return response.data.id;
  };

  const handlePixPayment = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const newOrderId = await createOrder();
      setOrderId(newOrderId);
      
      const paymentData = {
        amount: orderTotal,
        description: `Pedido Don't Disturb #${newOrderId.slice(0, 8)}`,
        payment_method: 'pix',
        email: formData.email,
        cpf_cnpj: formData.cpf_cnpj,
        order_id: newOrderId,
        customer_name: formData.name,
        customer_address: `${formData.address}, ${formData.number} - ${formData.neighborhood}, ${formData.city}/${formData.state}`,
        customer_phone: formData.phone
      };

      const response = await axios.post(`${API}/payments/pix`, paymentData);
      
      if (response.data.success) {
        setPaymentResult({
          type: 'pix',
          qr_code: response.data.qr_code,
          qr_code_base64: response.data.qr_code_base64,
          payment_id: response.data.payment_id
        });
        clearCart();
      }
    } catch (error) {
      console.error('PIX payment error:', error);
      const pixDetail = error.response?.data?.detail;
      toast.error(typeof pixDetail === 'string' ? pixDetail : 'Erro ao gerar PIX. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleBoletoPayment = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const newOrderId = await createOrder();
      setOrderId(newOrderId);
      
      const paymentData = {
        amount: orderTotal,
        description: `Pedido Don't Disturb #${newOrderId.slice(0, 8)}`,
        payment_method: 'boleto',
        email: formData.email,
        cpf_cnpj: formData.cpf_cnpj,
        order_id: newOrderId,
        customer_name: formData.name,
        customer_address: `${formData.address}, ${formData.number}`,
        customer_phone: formData.phone
      };

      const response = await axios.post(`${API}/payments/boleto`, paymentData);
      
      if (response.data.success) {
        setPaymentResult({
          type: 'boleto',
          barcode: response.data.barcode,
          external_resource_url: response.data.external_resource_url,
          due_date: response.data.due_date,
          payment_id: response.data.payment_id
        });
        clearCart();
      }
    } catch (error) {
      console.error('Boleto payment error:', error);
      const boletoDetail = error.response?.data?.detail;
      toast.error(typeof boletoDetail === 'string' ? boletoDetail : 'Erro ao gerar boleto. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardPayment = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const newOrderId = await createOrder();
      setOrderId(newOrderId);
      
      toast.info('Preparando pagamento com cartão...');
      setOrderComplete(true);
      clearCart();
      toast.success('Pedido criado! Entre em contato para finalizar o pagamento com cartão.');
      
    } catch (error) {
      console.error('Card payment error:', error);
      const cardDetail = error.response?.data?.detail;
      toast.error(typeof cardDetail === 'string' ? cardDetail : 'Erro ao processar cartão');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (paymentMethod === 'pix') {
      await handlePixPayment();
    } else if (paymentMethod === 'boleto') {
      await handleBoletoPayment();
    } else if (paymentMethod === 'credit_card') {
      await handleCardPayment();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Código copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Empty cart view
  if (cart.length === 0 && !orderComplete && !paymentResult && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24" data-testid="empty-cart-checkout">
        <div className="text-center">
          <Package className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-white mb-4">Seu carrinho está vazio</h2>
          <button
            onClick={() => navigate('/catalog')}
            className="bg-primary text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-colors"
            data-testid="browse-products-button"
          >
            Ver Produtos
          </button>
        </div>
      </div>
    );
  }

  // PIX Payment Result
  if (paymentResult?.type === 'pix') {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24 px-4" data-testid="pix-payment-result">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full text-center"
        >
          <QrCode className="h-16 w-16 text-primary mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-white mb-4">
            PAGUE COM <span className="text-primary">PIX</span>
          </h1>
          <p className="text-zinc-300 mb-6">
            Escaneie o QR Code abaixo com o app do seu banco ou copie o código PIX.
          </p>
          
          <div className="bg-white p-4 rounded-lg inline-block mb-6">
            {paymentResult.qr_code_base64 ? (
              <img 
                src={`data:image/png;base64,${paymentResult.qr_code_base64}`}
                alt="QR Code PIX"
                className="w-64 h-64 mx-auto"
              />
            ) : (
              <div className="w-64 h-64 bg-zinc-200 flex items-center justify-center">
                <p className="text-zinc-500">QR Code indisponível</p>
              </div>
            )}
          </div>
          
          {paymentResult.qr_code && (
            <div className="bg-zinc-900/50 border border-white/10 p-4 mb-6">
              <p className="text-xs text-zinc-400 mb-2">Código PIX Copia e Cola:</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={paymentResult.qr_code}
                  readOnly
                  className="flex-1 bg-zinc-800 border border-zinc-700 px-3 py-2 text-xs text-white font-mono truncate"
                />
                <button
                  onClick={() => copyToClipboard(paymentResult.qr_code)}
                  className="bg-primary text-black px-4 py-2 hover:bg-white transition-colors"
                >
                  {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
            </div>
          )}
          
          <p className="text-sm text-zinc-400 mb-6">
            ID do Pedido: <span className="font-mono text-primary">{orderId?.slice(0, 8)}</span>
          </p>
          
          <button
            onClick={() => navigate('/')}
            className="bg-zinc-800 text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-zinc-700 transition-colors"
          >
            Voltar ao Início
          </button>
        </motion.div>
      </div>
    );
  }

  // Boleto Payment Result
  if (paymentResult?.type === 'boleto') {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24 px-4" data-testid="boleto-payment-result">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full text-center"
        >
          <Barcode className="h-16 w-16 text-primary mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-white mb-4">
            PAGUE COM <span className="text-primary">BOLETO</span>
          </h1>
          <p className="text-zinc-300 mb-6">
            Copie o código de barras abaixo ou clique para baixar o boleto.
          </p>
          
          {paymentResult.barcode && (
            <div className="bg-zinc-900/50 border border-white/10 p-4 mb-6">
              <p className="text-xs text-zinc-400 mb-2">Código de Barras:</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={paymentResult.barcode}
                  readOnly
                  className="flex-1 bg-zinc-800 border border-zinc-700 px-3 py-2 text-xs text-white font-mono truncate"
                />
                <button
                  onClick={() => copyToClipboard(paymentResult.barcode)}
                  className="bg-primary text-black px-4 py-2 hover:bg-white transition-colors"
                >
                  {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
            </div>
          )}
          
          {paymentResult.external_resource_url && (
            <a
              href={paymentResult.external_resource_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-colors mb-6"
            >
              Baixar Boleto PDF
            </a>
          )}
          
          <p className="text-sm text-zinc-400 mb-6">
            ID do Pedido: <span className="font-mono text-primary">{orderId?.slice(0, 8)}</span>
          </p>
          
          <button
            onClick={() => navigate('/')}
            className="bg-zinc-800 text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-zinc-700 transition-colors"
          >
            Voltar ao Início
          </button>
        </motion.div>
      </div>
    );
  }

  // Order complete view
  if (orderComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24 px-4" data-testid="order-complete">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full text-center"
        >
          <CheckCircle2 className="h-24 w-24 text-primary mx-auto mb-6" />
          <h1 className="font-heading text-4xl font-bold text-white mb-4" data-testid="order-success-title">
            PEDIDO CONFIRMADO!
          </h1>
          <p className="text-zinc-300 text-lg mb-2">Seu pagamento foi aprovado.</p>
          <p className="font-mono text-sm text-zinc-400 mb-8" data-testid="order-id">
            ID do Pedido: {orderId}
          </p>
          <div className="bg-zinc-900/50 border border-white/10 p-8 mb-8">
            <p className="text-zinc-300 mb-4">
              Enviamos um e-mail de confirmação para <span className="text-primary font-bold">{formData.email}</span> com todos os detalhes do seu pedido.
            </p>
            <p className="text-zinc-400 text-sm">
              Em breve você receberá informações sobre o envio.
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-colors"
            data-testid="back-home-button"
          >
            Voltar ao Início
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 lg:mb-12"
        >
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="checkout-title">
            FINALIZAR <span className="text-primary">PEDIDO</span>
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Checkout Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="space-y-4 lg:space-y-6" data-testid="checkout-form">
              {/* Personal Info */}
              <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6">
                <h2 className="font-heading text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Informações Pessoais
                </h2>
                <div className="space-y-3 lg:space-y-4">
                  <div>
                    <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                      CPF ou CNPJ *
                    </label>
                    <input
                      type="text"
                      name="cpf_cnpj"
                      value={formData.cpf_cnpj}
                      onChange={handleInputChange}
                      placeholder="000.000.000-00"
                      maxLength={18}
                      className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                      required
                      data-testid="input-cpf"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                    <div>
                      <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                        required
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                        Telefone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                        required
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6">
                <h2 className="font-heading text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6 flex items-center gap-2">
                  <Truck className="h-5 w-5 text-primary" />
                  Endereço de Entrega
                </h2>
                <div className="space-y-3 lg:space-y-4">
                  <div>
                    <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                      CEP *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleInputChange}
                        placeholder="00000-000"
                        maxLength={9}
                        className="flex-1 bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                        required
                        data-testid="input-cep"
                      />
                      <button
                        type="button"
                        onClick={calculateShipping}
                        disabled={loadingShipping}
                        className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 h-11 lg:h-12 transition-colors disabled:opacity-50 text-sm"
                      >
                        {loadingShipping ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Calcular'}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                      Endereço *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Rua, Avenida..."
                      className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                      required
                      data-testid="input-address"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:gap-4">
                    <div>
                      <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                        Número *
                      </label>
                      <input
                        type="text"
                        name="number"
                        value={formData.number}
                        onChange={handleInputChange}
                        placeholder="123"
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                        data-testid="input-number"
                      />
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                        Complemento
                      </label>
                      <input
                        type="text"
                        name="complement"
                        value={formData.complement}
                        onChange={handleInputChange}
                        placeholder="Apto, Bloco..."
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                        data-testid="input-complement"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                      Bairro *
                    </label>
                    <input
                      type="text"
                      name="neighborhood"
                      value={formData.neighborhood}
                      onChange={handleInputChange}
                      className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                      required
                      data-testid="input-neighborhood"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 lg:gap-4">
                    <div>
                      <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                        Cidade *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                        required
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <label className="block text-xs lg:text-sm uppercase tracking-wider text-zinc-400 mb-2">
                        Estado *
                      </label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-11 lg:h-12 px-4 text-white text-sm lg:text-base"
                        required
                        data-testid="input-state"
                      >
                        <option value="">UF</option>
                        {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Options */}
              {shippingCalculated && shippingOptions.length > 0 && (
                <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6">
                  <h2 className="font-heading text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6 flex items-center gap-2">
                    <Package className="h-5 w-5 text-primary" />
                    Opções de Envio
                  </h2>
                  
                  {cartTotal >= freeShippingThreshold && (
                    <div className="bg-green-500/10 border border-green-500/30 p-3 mb-4 text-center">
                      <p className="text-green-500 text-sm font-bold">
                        🎉 FRETE GRÁTIS! Compras acima de R$ {freeShippingThreshold.toFixed(2)}
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    {shippingOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setSelectedShipping(option)}
                        className={`w-full p-3 lg:p-4 border transition-all text-left ${
                          selectedShipping?.id === option.id
                            ? 'border-primary bg-primary/10'
                            : 'border-zinc-700 hover:border-zinc-500'
                        }`}
                        data-testid={`shipping-option-${option.id}`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-white font-bold text-sm lg:text-base">{option.name}</p>
                            <p className="text-zinc-400 text-xs lg:text-sm">{option.company}</p>
                            <p className="text-zinc-500 text-xs">Prazo: {option.delivery_time} dias úteis</p>
                          </div>
                          <div className="text-right">
                            {option.free_shipping ? (
                              <p className="text-green-500 font-bold text-sm lg:text-base">GRÁTIS</p>
                            ) : (
                              <p className="text-primary font-mono font-bold text-sm lg:text-base">
                                R$ {option.price.toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Method */}
              <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6">
                <h2 className="font-heading text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Forma de Pagamento
                </h2>
                
                <div className="grid grid-cols-3 gap-2 lg:gap-3 mb-4 lg:mb-6">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('pix')}
                    className={`p-3 lg:p-4 border transition-all ${
                      paymentMethod === 'pix'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                    }`}
                    data-testid="payment-method-pix"
                  >
                    <QrCode className="h-6 w-6 lg:h-8 lg:w-8 mx-auto mb-1 lg:mb-2" />
                    <span className="text-xs lg:text-sm font-bold uppercase">PIX</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('boleto')}
                    className={`p-3 lg:p-4 border transition-all ${
                      paymentMethod === 'boleto'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                    }`}
                    data-testid="payment-method-boleto"
                  >
                    <Barcode className="h-6 w-6 lg:h-8 lg:w-8 mx-auto mb-1 lg:mb-2" />
                    <span className="text-xs lg:text-sm font-bold uppercase">Boleto</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('credit_card')}
                    className={`p-3 lg:p-4 border transition-all ${
                      paymentMethod === 'credit_card'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                    }`}
                    data-testid="payment-method-card"
                  >
                    <CreditCard className="h-6 w-6 lg:h-8 lg:w-8 mx-auto mb-1 lg:mb-2" />
                    <span className="text-xs lg:text-sm font-bold uppercase">Cartão</span>
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  {paymentMethod === 'pix' && (
                    <motion.div
                      key="pix-info"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-green-500/10 border border-green-500/30 p-3 lg:p-4"
                    >
                      <p className="text-green-500 text-xs lg:text-sm">
                        <strong>PIX:</strong> Pagamento instantâneo. Você receberá um QR Code para escanear.
                      </p>
                    </motion.div>
                  )}
                  
                  {paymentMethod === 'boleto' && (
                    <motion.div
                      key="boleto-info"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-blue-500/10 border border-blue-500/30 p-3 lg:p-4"
                    >
                      <p className="text-blue-500 text-xs lg:text-sm">
                        <strong>Boleto:</strong> Vencimento em 3 dias úteis. Confirmação em até 2 dias úteis.
                      </p>
                    </motion.div>
                  )}
                  
                  {paymentMethod === 'credit_card' && (
                    <motion.div
                      key="card-info"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-purple-500/10 border border-purple-500/30 p-3 lg:p-4"
                    >
                      <p className="text-purple-500 text-xs lg:text-sm">
                        <strong>Cartão:</strong> Aceitamos Visa, Mastercard, Amex e Elo. Até 12x.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Submit Button */}
              <button
                onClick={handlePayment}
                disabled={loading || !shippingCalculated || !selectedShipping}
                className="w-full bg-primary text-black font-bold uppercase tracking-wider py-3 lg:py-4 text-base lg:text-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-order-button"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 lg:h-6 lg:w-6 animate-spin" />
                    Processando...
                  </div>
                ) : !shippingCalculated ? (
                  'Calcule o frete primeiro'
                ) : paymentMethod === 'pix' ? (
                  `Pagar R$ ${orderTotal.toFixed(2)} com PIX`
                ) : paymentMethod === 'boleto' ? (
                  `Gerar Boleto de R$ ${orderTotal.toFixed(2)}`
                ) : (
                  `Pagar R$ ${orderTotal.toFixed(2)} com Cartão`
                )}
              </button>
            </div>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* Upsell Products */}
            <CheckoutUpsell 
              cart={cart} 
              onAddToCart={refreshCart}
              sessionId={sessionId}
            />
            
            <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6 sticky top-24" data-testid="order-summary">
              <h2 className="font-heading text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6">Resumo do Pedido</h2>
              
              <div className="space-y-3 lg:space-y-4 mb-4 lg:mb-6">
                {cart.map(item => (
                  <div key={item.cart_id} className="flex gap-3 lg:gap-4" data-testid={`summary-item-${item.cart_id}`}>
                    <img
                      src={item.product.image}
                      alt={item.product.name}
                      className="w-14 h-14 lg:w-16 lg:h-16 object-cover border border-white/10"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-xs lg:text-sm font-bold text-white truncate">{item.product.name}</h3>
                      <p className="text-[10px] lg:text-xs text-zinc-400">Tamanho: {item.size} | Qtd: {item.quantity}</p>
                      <p className="font-mono text-xs lg:text-sm text-primary mt-1">
                        R$ {(item.product.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between text-zinc-400 text-sm">
                  <span>Subtotal</span>
                  <span className="font-mono">R$ {cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-zinc-400 text-sm">
                  <span>Frete</span>
                  {selectedShipping ? (
                    selectedShipping.free_shipping ? (
                      <span className="font-mono text-green-500">GRÁTIS</span>
                    ) : (
                      <span className="font-mono">R$ {shippingCost.toFixed(2)}</span>
                    )
                  ) : (
                    <span className="text-zinc-500 text-xs">Calcule o frete</span>
                  )}
                </div>
                
                {cartTotal < freeShippingThreshold && (
                  <p className="text-xs text-zinc-500 text-center py-2">
                    Falta R$ {(freeShippingThreshold - cartTotal).toFixed(2)} para frete grátis!
                  </p>
                )}
                
                <div className="flex justify-between text-lg lg:text-xl font-bold text-white pt-4 border-t border-white/10">
                  <span className="font-heading">Total</span>
                  <span className="font-mono text-primary" data-testid="order-total">
                    R$ {orderTotal.toFixed(2)}
                  </span>
                </div>
              </div>
              
              {/* Security Badge */}
              <div className="mt-4 lg:mt-6 pt-4 lg:pt-6 border-t border-white/10">
                <div className="flex items-center gap-2 text-zinc-500 text-xs">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Pagamento seguro via Mercado Pago
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
